import { lazy } from 'react';
import retry from './retry';

// After the retries are exhausted, a lazy chunk that still won't load almost
// always means one of two things: the running SPA shell is stale (a deploy
// moved the hashed chunk out from under this long-lived tab) or a transient
// origin blip is in progress. A single full reload fetches a fresh index.html
// + asset manifest and recovers both cases — far better than leaving the user
// on a blank/broken view that needs a manual refresh (the 2026-06-28 symptom).
// NB this recovery relies on index.html being served `no-cache` at the edge
// (Track A guarantees that); were that reverted, a reload could re-fetch the
// same stale shell and the once-per-session guard would then strand the user.
//
// Guards:
//   - sessionStorage flag -> fire AT MOST ONCE per session; if the chunk still
//     fails after the reload it is genuinely gone (a real outage), so surface
//     the error instead of looping.
//   - in-memory flag -> when several chunks fail in the SAME page load, hold
//     every one of them (never-settling promise) once a reload is underway, so
//     the losers of the race to set the sessionStorage flag don't flash the
//     error boundary before the page tears down.
//   - if sessionStorage is unavailable or throws (SSR / Safari private mode),
//     we cannot guarantee loop-safety, so we DO NOT reload — just surface it.
// (#518 / #476)
export const CHUNK_RELOAD_FLAG = 'chunkReloadAttempted';

let reloadInitiatedThisLoad = false;

export const reloadOnceThen = (error) => {
  // A reload is already underway in this page load -> hold this concurrent
  // failure until the browser navigates away, rather than throwing to the
  // error boundary.
  if (reloadInitiatedThisLoad) {
    return new Promise(() => {});
  }

  try {
    const storage = typeof window !== 'undefined' ? window.sessionStorage : null;
    if (!storage) throw error;
    if (storage.getItem(CHUNK_RELOAD_FLAG)) {
      // Already reloaded once this session and it still fails -> genuinely gone.
      throw error;
    }
    storage.setItem(CHUNK_RELOAD_FLAG, '1');
  } catch (e) {
    // Storage unavailable/throwing, or we re-threw above: can't guarantee
    // loop-safety -> surface the original chunk error, do not reload.
    throw error;
  }

  reloadInitiatedThisLoad = true;
  window.location.reload();
  // Hold Suspense until the reload navigates away — never resolve/reject, so no
  // error UI flashes in the moment before the browser tears the page down.
  return new Promise(() => {});
};

// Test-only: reset the in-memory per-page-load guard so unit tests stay
// order-independent (the flag is intentionally module-scoped at runtime — it
// must survive across all chunk failures within one page load).
export const __resetReloadGuardForTests = () => {
  reloadInitiatedThisLoad = false;
};

const lazyLoad = (fn) => lazy(() => retry(fn, 4, 2500).catch(reloadOnceThen));

export default lazyLoad;
